import { useState, type MouseEvent } from 'react';
import { googleMapsUrl, wazeUrl } from '@shared/maps';
import { GoogleMapsIcon, WazeIcon, MenuCardIcon } from './Icons';
import { ActionMenu } from './ActionMenu';
import { MenuSheet } from './MenuSheet';
import type { Place } from '@shared/types';
import type { PlaceWithState } from '@/types/api';

// Compact nav-icon cluster + more-menu trigger for every place row.
//
// stopPropagation keeps a parent card/row from treating the tap as
// "open this place". Do NOT preventDefault on the Google Maps / Waze
// <a>s — that cancelled the href, so the icons did nothing. The cluster
// wrapper also used to preventDefault on every bubbled click, which
// killed child-link navigation even if the <a> itself did not. Buttons
// still preventDefault so a leftover parent <Link> does not navigate.

interface PlaceRowNavProps {
  place: Place | PlaceWithState;
}

function stopCardNav(e: MouseEvent) {
  e.stopPropagation();
}

function stopCardNavAndDefault(e: MouseEvent) {
  e.stopPropagation();
  e.preventDefault();
}

function onClusterClick(e: MouseEvent) {
  e.stopPropagation();
  // Padding between icons: cancel a parent <a>'s navigation. Clicks that
  // originated on a child <a> must keep their default (open maps).
  if (e.target === e.currentTarget) e.preventDefault();
}

export function PlaceRowNav({ place }: PlaceRowNavProps) {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Only restaurants get a menu affordance — a dentist has no menu, and an
  // icon that always opens an empty sheet trains people to ignore it.
  const isFood = place.category === 'food';
  return (
    <>
      <div className="place-nav-icons" onClick={onClusterClick} onMouseDown={stopCardNav}>
        {isFood && (
          <button
            type="button"
            aria-label={`Menu for ${place.name}`}
            onClick={(e) => {
              stopCardNavAndDefault(e);
              setMenuOpen(true);
            }}
            onMouseDown={stopCardNav}
          >
            <MenuCardIcon />
          </button>
        )}
        <a
          href={googleMapsUrl(place.address)}
          target="_blank"
          rel="noreferrer"
          aria-label={`Directions to ${place.name} in Google Maps`}
          onClick={stopCardNav}
          onMouseDown={stopCardNav}
        >
          <GoogleMapsIcon />
        </a>
        <a
          href={wazeUrl(place.latitude, place.longitude)}
          target="_blank"
          rel="noreferrer"
          aria-label={`Directions to ${place.name} in Waze`}
          onClick={stopCardNav}
          onMouseDown={stopCardNav}
        >
          <WazeIcon />
        </a>
        <button
          type="button"
          aria-label={`More actions for ${place.name}`}
          onClick={(e) => {
            stopCardNavAndDefault(e);
            setOpen(true);
          }}
          onMouseDown={stopCardNav}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <circle cx="5" cy="12" r="2" fill="currentColor" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <circle cx="19" cy="12" r="2" fill="currentColor" />
          </svg>
        </button>
      </div>
      <ActionMenu place={place} open={open} onClose={() => setOpen(false)} />
      {isFood && (
        <MenuSheet
          slug={place.slug}
          name={place.name}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
